import { For, Show } from 'solid-js';

function clampPct(n: number): number {
  return Math.min(100, Math.max(0, n));
}

/**
 * Continuous-arc instrument, deliberately shaped unlike SegRing so the GPU and CPU
 * gauges never read as the same subsystem at a glance. The rim is decorative only —
 * it must never be the sole carrier of a value (screen readers get the aria-label).
 */
export function ArcRing(props: {
  value: number | null;
  label: string;
  size?: number;
  rim?: boolean;
}) {
  const size = () => props.size ?? 128;
  const r = () => size() / 2 - 12;
  const circumference = () => 2 * Math.PI * r();
  const offset = () =>
    props.value == null ? circumference() : circumference() * (1 - clampPct(props.value) / 100);

  return (
    <div class="relative flex-none" style={{ width: `${size()}px`, height: `${size()}px` }}>
      <Show when={props.rim !== false}>
        <svg
          class="hud-rim absolute inset-0"
          width={size()}
          height={size()}
          viewBox={`0 0 ${size()} ${size()}`}
          aria-hidden="true"
        >
          <circle
            cx={size() / 2}
            cy={size() / 2}
            r={size() / 2 - 4}
            fill="none"
            stroke="var(--line)"
            stroke-width="1"
            stroke-dasharray="3 9"
          />
        </svg>
      </Show>
      <svg
        class="absolute inset-0 -rotate-90"
        width={size()}
        height={size()}
        viewBox={`0 0 ${size()} ${size()}`}
        role="img"
        aria-label={`${props.label}: ${props.value == null ? 'unknown' : `${props.value}%`}`}
      >
        <circle cx={size() / 2} cy={size() / 2} r={r()} fill="none" stroke="var(--track)" stroke-width="8" />
        <circle
          data-arc
          cx={size() / 2}
          cy={size() / 2}
          r={r()}
          fill="none"
          stroke="var(--holo)"
          stroke-width="8"
          stroke-linecap="round"
          stroke-dasharray={String(circumference())}
          stroke-dashoffset={offset()}
          class="transition-[stroke-dashoffset] duration-500"
        />
      </svg>
      <div class="absolute inset-0 flex flex-col items-center justify-center">
        <b class="text-[1.75rem] font-light tabular-nums text-holo">
          {props.value == null ? '—' : `${props.value}%`}
        </b>
        <span class="hud-mono text-[0.5625rem] uppercase tracking-[0.26em] text-faint">
          {props.label}
        </span>
      </div>
    </div>
  );
}

/**
 * Segmented instrument for the CPU: `segments` is meant to carry the host's real
 * hardware-thread count, so each tick maps to one thread rather than an arbitrary
 * slice. A non-positive or non-finite count falls back to 12 rather than rendering
 * an empty ring (a caller may forward an unread thread count as 0).
 */
export function SegRing(props: {
  value: number | null;
  label: string;
  segments?: number;
  size?: number;
}) {
  const size = () => props.size ?? 96;
  const count = () => {
    const n = props.segments;
    return n != null && Number.isFinite(n) && n > 0 ? n : 12;
  };
  const lit = () => {
    if (props.value == null || props.value <= 0) return 0;
    return Math.max(1, Math.ceil((clampPct(props.value) / 100) * count()));
  };

  return (
    <div class="relative flex-none" style={{ width: `${size()}px`, height: `${size()}px` }}>
      <svg
        width={size()}
        height={size()}
        viewBox={`0 0 ${size()} ${size()}`}
        role="img"
        aria-label={`${props.label}: ${props.value == null ? 'unknown' : `${props.value}%`}`}
      >
        <For each={Array.from({ length: count() }, (_, i) => i)}>
          {(i) => (
            <rect
              data-seg={i < lit() ? 'on' : 'off'}
              x={size() / 2 - 2.5}
              y={5}
              width="5"
              height="15"
              rx="1.5"
              fill={i < lit() ? 'var(--holo)' : 'var(--track)'}
              transform={`rotate(${(i * 360) / count()} ${size() / 2} ${size() / 2})`}
            />
          )}
        </For>
      </svg>
      <div class="absolute inset-0 flex flex-col items-center justify-center">
        <b class="text-[1.375rem] font-light tabular-nums text-holo">
          {props.value == null ? '—' : `${props.value}%`}
        </b>
        <span class="hud-mono text-[0.5625rem] uppercase tracking-[0.26em] text-faint">
          {props.label}
        </span>
      </div>
    </div>
  );
}
